import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, serverTimestamp, getDocs, query, orderBy, limit, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useNavigate, useParams, Link, useSearchParams } from 'react-router-dom';

function CreateSchedule({ user, userData, editMode }) {
    const teamId = userData?.teamId;
    const [title, setTitle] = useState('');
    const [date, setDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isMultiDay, setIsMultiDay] = useState(false);
    const [time, setTime] = useState('');
    const [meetupPerson, setMeetupPerson] = useState('');
    const [destination, setDestination] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [fetchingEdit, setFetchingEdit] = useState(false);

    // Image state
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [existingImageUrl, setExistingImageUrl] = useState(null);

    // Voice state
    const [isRecording, setIsRecording] = useState(false);
    const [audioBlob, setAudioBlob] = useState(null);
    const [audioUrl, setAudioUrl] = useState(null);
    const [existingVoiceUrl, setExistingVoiceUrl] = useState(null);
    const [recordingTime, setRecordingTime] = useState(0);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const timerRef = useRef(null);

    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();

    // Pre-fill date from query param (from calendar day click)
    useEffect(() => {
        if (!editMode) {
            const preDate = searchParams.get('date');
            if (preDate) {
                setDate(preDate);
            }
        }
    }, [editMode, searchParams]);
    // Load existing schedule data if in edit mode
    useEffect(() => {
        if (editMode && id) {
            const fetchSchedule = async () => {
                setFetchingEdit(true);
                try {
                    const docRef = doc(db, 'teams', teamId, 'schedules', id);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setTitle(data.title || '');
                        setDate(data.date || '');
                        setTime(data.time || '');
                        setMeetupPerson(data.meetupPerson || '');
                        setDestination(data.destination || '');
                        setDescription(data.description || '');
                        if (data.endDate) {
                            setEndDate(data.endDate);
                            setIsMultiDay(true);
                        }
                        if (data.imageUrl) {
                            setExistingImageUrl(data.imageUrl);
                        }
                        if (data.voiceUrl) {
                            setExistingVoiceUrl(data.voiceUrl);
                        }
                    } else {
                        alert('Хуваарь олдсонгүй');
                        navigate('/');
                    }
                } catch (err) {
                    console.error('Error fetching schedule:', err);
                    alert('Алдаа гарлаа');
                    navigate('/');
                } finally {
                    setFetchingEdit(false);
                }
            };
            fetchSchedule();
        }
    }, [editMode, id, navigate]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
        };
    }, []);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setImagePreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const removeImage = () => {
        setImageFile(null);
        setImagePreview(null);
        setExistingImageUrl(null);
    };

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    audioChunksRef.current.push(e.data);
                }
            };

            mediaRecorder.onstop = () => {
                const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                setAudioBlob(blob);
                setAudioUrl(URL.createObjectURL(blob));
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
            setRecordingTime(0);
            timerRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } catch (err) {
            console.error('Микрофон ашиглах эрхгүй:', err);
            alert('Микрофон ашиглах зөвшөөрөл өгнө үү');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
        setIsRecording(false);
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const removeAudio = () => {
        setAudioBlob(null);
        setAudioUrl(null);
        setExistingVoiceUrl(null);
        setRecordingTime(0);
    };

    const formatRecordingTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let imageUrl = existingImageUrl || null;
            let voiceUrl = existingVoiceUrl || null;

            // Generate a temp ID for new docs  
            const tempId = editMode ? id : Date.now().toString();

            // Upload image if selected
            if (imageFile) {
                const imgRef = ref(storage, `schedules/${tempId}/image_${Date.now()}`);
                await uploadBytes(imgRef, imageFile);
                imageUrl = await getDownloadURL(imgRef);
            }

            // Upload voice if recorded
            if (audioBlob) {
                const voiceRef = ref(storage, `schedules/${tempId}/voice_${Date.now()}`);
                await uploadBytes(voiceRef, audioBlob);
                voiceUrl = await getDownloadURL(voiceRef);
            }

            const scheduleData = {
                title,
                date,
                endDate: isMultiDay ? endDate : null,
                time,
                meetupPerson,
                destination,
                description,
                imageUrl,
                voiceUrl,
            };

            if (editMode && id) {
                const docRef = doc(db, 'teams', teamId, 'schedules', id);
                await updateDoc(docRef, {
                    ...scheduleData,
                    updatedAt: serverTimestamp()
                });
            } else {
                let newOrder = 0;
                const q = query(collection(db, 'teams', teamId, 'schedules'), orderBy('order', 'desc'), limit(1));
                const querySnapshot = await getDocs(q);
                if (!querySnapshot.empty) {
                    newOrder = querySnapshot.docs[0].data().order + 100;
                }

                await addDoc(collection(db, 'teams', teamId, 'schedules'), {
                    ...scheduleData,
                    order: newOrder,
                    creatorId: user.uid,
                    creatorName: userData?.name || user.displayName,
                    createdAt: serverTimestamp()
                });
            }

            navigate('/');
        } catch (err) {
            console.error("Error saving document: ", err);
            alert("Алдаа гарлаа. Та дахин оролдоно уу.");
        } finally {
            setLoading(false);
        }
    };

    if (fetchingEdit) {
        return (
            <div className="page-container">
                <div className="loading-spinner-inline">
                    <span className="spinner"></span>
                    <p className="mt-2" style={{ color: 'var(--text-muted)' }}>Ачаалж байна...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container mobile-page">
            <div className="mobile-page-header">
                <h1 className="mobile-page-title">{editMode ? '✏️ Засварлах' : '✨ Шинэ хуваарь'}</h1>
                <Link to="/" className="cal-back-btn-header">← Буцах</Link>
            </div>

            <div className="glass-card create-form-card">
                <h3>{editMode ? 'Хуваарийн мэдээллийг засварлана уу' : 'Хуваарийн мэдээллийг оруулна уу'}</h3>
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>📌 Гарчиг</label>
                        <input
                            type="text"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            required
                            placeholder="Жишээ: Төслийн уулзалт"
                        />
                    </div>

                    {/* Date toggle */}
                    <div className="form-group">
                        <div className="date-toggle">
                            <button
                                type="button"
                                className={`date-toggle__btn ${!isMultiDay ? 'date-toggle__btn--active' : ''}`}
                                onClick={() => { setIsMultiDay(false); setEndDate(''); }}
                            >
                                📅 Нэг өдөр
                            </button>
                            <button
                                type="button"
                                className={`date-toggle__btn ${isMultiDay ? 'date-toggle__btn--active' : ''}`}
                                onClick={() => setIsMultiDay(true)}
                            >
                                📆 Олон өдөр
                            </button>
                        </div>
                    </div>

                    <div className="form-row">
                        <div className="form-group">
                            <label>{isMultiDay ? '📅 Эхлэх огноо' : '📅 Огноо'}</label>
                            <input
                                type="date"
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                                required
                            />
                        </div>
                        {isMultiDay && (
                            <div className="form-group">
                                <label>📅 Дуусах огноо</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    required
                                    min={date}
                                />
                            </div>
                        )}
                        <div className="form-group">
                            <label>🕐 Цаг</label>
                            <div className="time-picker-24h">
                                <select className="time-picker-24h__select" value={time ? time.split(':')[0] : ''} onChange={(e) => { const h = e.target.value; const m = time ? time.split(':')[1] : '00'; setTime(h ? `${h}:${m}` : ''); }} required>
                                    <option value="">Цаг</option>
                                    {Array.from({ length: 24 }, (_, i) => (<option key={i} value={String(i).padStart(2, '0')}>{String(i).padStart(2, '0')}</option>))}
                                </select>
                                <span className="time-picker-24h__sep">:</span>
                                <select className="time-picker-24h__select" value={time ? time.split(':')[1] : ''} onChange={(e) => { const h = time ? time.split(':')[0] : '00'; const m = e.target.value; setTime(m ? `${h}:${m}` : ''); }} required>
                                    <option value="">Мин</option>
                                    {Array.from({ length: 12 }, (_, i) => (<option key={i} value={String(i * 5).padStart(2, '0')}>{String(i * 5).padStart(2, '0')}</option>))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-group">
                        <label>👤 Уулзах хүн</label>
                        <input
                            type="text"
                            value={meetupPerson}
                            onChange={(e) => setMeetupPerson(e.target.value)}
                            placeholder="Жишээ: С.Бат"
                        />
                    </div>

                    <div className="form-group">
                        <label>📍 Очих газар / Байршил</label>
                        <input
                            type="text"
                            value={destination}
                            onChange={(e) => setDestination(e.target.value)}
                            placeholder="Жишээ: Blue Sky Tower"
                        />
                    </div>

                    <div className="form-group">
                        <label>📝 Дэлгэрэнгүй тайлбар <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.8rem' }}>(заавал биш)</span></label>
                        <textarea
                            rows="3"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Нэмэлт мэдээлэл оруулах..."
                        ></textarea>
                    </div>

                    {/* Image upload */}
                    <div className="form-group">
                        <label>🖼️ Зураг хавсаргах <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.8rem' }}>(заавал биш)</span></label>
                        {!imagePreview && !existingImageUrl ? (
                            <label className="image-picker">
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    style={{ display: 'none' }}
                                />
                                <div className="image-picker__placeholder">
                                    <span className="image-picker__icon">📷</span>
                                    <span className="image-picker__text">Зураг сонгох</span>
                                </div>
                            </label>
                        ) : (
                            <div className="image-picker__preview-wrap">
                                <img
                                    src={imagePreview || existingImageUrl}
                                    alt="Preview"
                                    className="image-picker__preview"
                                />
                                <button type="button" className="image-picker__remove" onClick={removeImage}>✕</button>
                            </div>
                        )}
                    </div>

                    {/* Voice recorder */}
                    <div className="form-group">
                        <label>🎙️ Дуу бичлэг <span style={{ color: 'var(--text-muted)', fontWeight: 400, fontSize: '0.8rem' }}>(заавал биш)</span></label>
                        {!audioUrl && !existingVoiceUrl ? (
                            <div className="voice-recorder">
                                {!isRecording ? (
                                    <button type="button" className="voice-recorder__btn" onClick={startRecording}>
                                        <span className="voice-recorder__mic">🎙️</span>
                                        Бичлэг эхлэх
                                    </button>
                                ) : (
                                    <div className="voice-recorder__active">
                                        <span className="voice-recorder__pulse"></span>
                                        <span className="voice-recorder__timer">{formatRecordingTime(recordingTime)}</span>
                                        <button type="button" className="voice-recorder__stop" onClick={stopRecording}>
                                            ⏹ Зогсоох
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="voice-recorder__playback">
                                <audio controls src={audioUrl || existingVoiceUrl} className="voice-recorder__audio" />
                                <button type="button" className="voice-recorder__remove" onClick={removeAudio}>✕</button>
                            </div>
                        )}
                    </div>

                    <button type="submit" className="btn btn-primary w-100 mt-3" disabled={loading}>
                        {loading ? (
                            <>
                                <span className="spinner"></span>
                                Хадгалж байна...
                            </>
                        ) : (
                            editMode ? '💾 Хадгалах' : '✨ Хуваарь үүсгэх'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default CreateSchedule;
